import { z } from 'zod';
import { ProductTaxClassSchema } from './types';

/**
 * United States sales-tax reference table, one row per state plus DC.
 *
 * Rates: Tax Foundation "State and Local Sales Tax Rates, Midyear 2026"
 * (July 1, 2026 figures; no statewide rate changed between January and July
 * 2026). `stateRatePercent` is the statutory state rate; the Tax Foundation
 * folds a mandatory statewide local add-on into the state figure for CA
 * (1.25%), UT (1.25%) and VA (1%), and so does this table, flagged in
 * `mandatoryLocalIncludedPercent`. Where the Tax Foundation rounds
 * (MO 4.225, NJ 6.625, NM 4.875, MN 6.875) the statutory rate is used.
 *
 * Local rates are AVERAGES. A ZIP code does not identify a taxing
 * jurisdiction (many ZIPs straddle county and district lines), so this
 * table cannot compute the exact local rate for an address. That is what
 * Stripe Tax's rooftop lookup is for. See docs/sales-tax/README.md.
 *
 * Sourcing: what a REMOTE seller or marketplace shipping into the state
 * does is destination sourcing in every state. The origin/hybrid flags
 * only matter when seller and buyer are in the same state.
 *
 * Marketplace facilitator (MPF) law: every sales-tax state plus DC and the
 * Alaska ARSSTC jurisdictions require a marketplace to collect on behalf of
 * its sellers once over the economic-nexus threshold. `threshold` is the
 * general remote-seller threshold, which governs marketplaces too unless
 * `marketplaceThresholdNote` says otherwise.
 *
 * Exemptions are keyed by product tax class. Used goods sold through a
 * marketplace facilitator get NO occasional/casual-sale relief (Minnesota
 * fs132, Illinois GIL ST 21-0003), so nothing here distinguishes new from
 * used. Sales-tax holidays are deliberately not modelled.
 *
 * Anything marked in `verify` was not confirmed against a primary 2026
 * source at the time of writing and must be checked before it is relied on.
 */

export const US_STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY',
  'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH',
  'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
] as const;
export const UsStateCodeSchema = z.enum(US_STATE_CODES);
export type UsStateCode = z.infer<typeof UsStateCodeSchema>;

/** How a product class is treated relative to the standard rate. */
export const TaxTreatmentSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('taxable') }),
  z.object({ kind: z.literal('exempt'), note: z.string().optional() }),
  /** Taxed at this rate instead of the state rate (local rates still apply unless `localsExempt`). */
  z.object({ kind: z.literal('reduced'), ratePercent: z.number().min(0), localsExempt: z.boolean().default(false), note: z.string().optional() }),
  /**
   * Exempt when the unit price is below a threshold. `excessOnly` = only the
   * part above the threshold is taxed (MA, RI); otherwise the whole item is
   * taxed once at or above it (NY). `stateOnly` = the exemption applies to
   * the state portion; local jurisdictions may still tax (NY outside NYC).
   */
  z.object({
    kind: z.literal('exempt_below'),
    thresholdCents: z.number().int().positive(),
    excessOnly: z.boolean().default(false),
    stateOnly: z.boolean().default(false),
    note: z.string().optional(),
  }),
  /** Standard rate, but a higher rate on items priced at or above a threshold (CT luxury). */
  z.object({ kind: z.literal('rate_above'), thresholdCents: z.number().int().positive(), ratePercent: z.number().min(0), note: z.string().optional() }),
  /** Not collected at the point of sale; the DMV collects at titling. */
  z.object({ kind: z.literal('taxed_at_registration') }),
]);
export type TaxTreatment = z.infer<typeof TaxTreatmentSchema>;

/**
 * How a state treats the delivery charge, resolved for how Sellwild actually
 * ships rather than left as "it depends".
 *
 * Every Sellwild sale itemises freight on the receipt, prices it at the carrier
 * label's own cost, and moves the goods by common carrier (a Shippo USPS label)
 * after the sale is made. Those are the facts the state tests turn on, so each
 * row answers the test rather than deferring it. A `conditional` value used to
 * sit here and the engine resolved it to taxable, which over-collected freight
 * tax in seven states whose own notes said the charge was exempt.
 *
 * Where a state's test asks for something Sellwild does NOT satisfy -- Florida,
 * Illinois and Missouri exempt freight only when the buyer could have avoided
 * it by collecting in person, and a shipped-only marketplace sale cannot -- the
 * row says `taxable` and its note says why.
 */
export const ShippingTaxabilitySchema = z.enum([
  'taxable', // delivery charge follows the taxability of the goods
  'exempt_if_separately_stated', // excluded when itemised on the invoice (Sellwild always itemises)
  'not_applicable', // no sales tax
]);
export type ShippingTaxability = z.infer<typeof ShippingTaxabilitySchema>;

export const UsStateTaxRuleSchema = z.object({
  code: UsStateCodeSchema,
  name: z.string(),
  stateRatePercent: z.number().min(0),
  mandatoryLocalIncludedPercent: z.number().min(0).default(0),
  avgLocalRatePercent: z.number(),
  maxLocalRatePercent: z.number().min(0),
  avgCombinedRatePercent: z.number().min(0),
  hasLocalTaxes: z.boolean(),
  sourcing: z.enum(['destination', 'origin', 'hybrid', 'none']),
  /**
   * Whether this table can put a defensible number on a sale here.
   *
   * False for Alaska alone. Alaska has no statewide rate and its boroughs are
   * the whole story: most of the population pays nothing -- Anchorage levies
   * zero -- while a few boroughs charge up to 7.85%. Averaging those into one
   * 1.82% and applying it to every address charges an Anchorage buyer tax they
   * do not owe, to a jurisdiction that cannot be remitted to. A registered but
   * unquotable state answers `unavailable`, which the payment route logs, so
   * the sale is visible rather than silently guessed at.
   *
   * Distinct from `sourcing: 'none'`, which means the state levies no tax at
   * all. Alaska levies plenty; we just cannot tell where.
   */
  quotable: z.boolean().default(true),
  marketplaceFacilitator: z
    .object({
      effective: z.string(),
      thresholdSalesUsd: z.number().int().positive().nullable(),
      thresholdTransactions: z.number().int().positive().nullable(),
      /** 'or' = either test triggers nexus; 'and' = both must be met. */
      thresholdJoin: z.enum(['or', 'and']).default('or'),
      marketplaceThresholdNote: z.string().optional(),
    })
    .nullable(),
  /**
   * A flat rate the state lets a marketplace facilitator collect INSTEAD of the
   * state rate and every local rate, because it registered under a simplified
   * programme rather than as an ordinary retailer.
   *
   * Alabama is the only one. Its Simplified Sellers Use Tax is a single 8% on
   * every retail sale delivered into the state, and collecting it discharges
   * the marketplace, the seller and the buyer from all other state and local
   * sales and use tax on that sale (Ala. Code 40-23-191 et seq.). So Alabama
   * needs no local rate lookup and never did: the table's old 4% state plus a
   * 5.46% local average came to 9.46% and over-charged every Alabama buyer by
   * a point and a half.
   */
  simplifiedMarketplaceRate: z
    .object({
      ratePercent: z.number().min(0),
      name: z.string(),
      /** Filed under this programme code rather than a jurisdiction's. */
      code: z.string().max(16),
      note: z.string(),
    })
    .optional(),
  shipping: ShippingTaxabilitySchema,
  shippingNote: z.string().optional(),
  /** Flat per-order fee on taxable deliveries (CO, MN). Cents. */
  retailDeliveryFee: z.object({ feeCents: z.number().int().positive(), minOrderCents: z.number().int().min(0) }).optional(),
  exemptions: z.partialRecord(ProductTaxClassSchema, TaxTreatmentSchema).default({}),
  notes: z.array(z.string()).default([]),
  verify: z.array(z.string()).default([]),
});
export type UsStateTaxRule = z.infer<typeof UsStateTaxRuleSchema>;
type UsStateTaxRuleInput = z.input<typeof UsStateTaxRuleSchema>;

const EXEMPT = { kind: 'exempt' } as const;
const DMV = { kind: 'taxed_at_registration' } as const;

const RAW_RULES: UsStateTaxRuleInput[] = [
  {
    code: 'AL', name: 'Alabama', stateRatePercent: 4.0, avgLocalRatePercent: 5.46, maxLocalRatePercent: 8.0, avgCombinedRatePercent: 9.46,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-01-01', thresholdSalesUsd: 250_000, thresholdTransactions: null },
    simplifiedMarketplaceRate: {
      ratePercent: 8.0,
      name: 'Simplified Sellers Use Tax',
      code: 'SSUT',
      note: 'A marketplace facilitator over the $250,000 threshold registers for SSUT and collects a flat 8% in place of all state and local sales and use tax (Ala. Code 40-23-191 et seq.).',
    },
    shipping: 'exempt_if_separately_stated', shippingNote: 'Exempt only if separately stated AND shipped by common carrier; taxable when delivered in the seller\'s own vehicle.',
    exemptions: { groceries: { kind: 'reduced', ratePercent: 2.0, note: 'State grocery rate 2% from 2025-09-01; locals apply.' }, motor_vehicle: DMV },
    notes: ['Diapers and period products exempt 2025-09-01 through 2028-08-31.', 'Digital goods taxed.'],
  },
  {
    code: 'AK', name: 'Alaska', stateRatePercent: 0, avgLocalRatePercent: 1.82, maxLocalRatePercent: 7.85, avgCombinedRatePercent: 1.82,
    // Alaska has NO statewide rate, and its boroughs are the whole story: most
    // of the population pays nothing (Anchorage levies zero), a few boroughs
    // charge up to 7.85%. Averaging those into one 1.82% and applying it to
    // every address charged an Anchorage buyer 1.82% they do not owe, which is
    // over-collection with no jurisdiction to remit it to. `sourcing: 'none'`
    // makes the table answer zero here until an ARSSTC rooftop lookup exists
    // to place the buyer in an actual borough. Under-collecting in Juneau is
    // the safer of the two errors by a distance.
    hasLocalTaxes: true, sourcing: 'destination', quotable: false,
    marketplaceFacilitator: { effective: '2020-02-03', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: 'Alaska Remote Seller Sales Tax Commission (ARSSTC) uniform code; statewide gross including marketplace sales. 200-transaction test dropped 2025-01-01. Collecting here needs a rooftop borough lookup, not this average.' },
    shipping: 'taxable', shippingNote: 'No state tax; local treatment follows the ARSSTC uniform code, which taxes the delivery charge on a taxable sale. Taxable where a borough levies.',
    exemptions: { motor_vehicle: DMV },
    notes: ['No state sales tax; roughly 100 local jurisdictions collect through ARSSTC.'],
    verify: ['Local shipping treatment under the ARSSTC uniform code.'],
  },
  {
    code: 'AZ', name: 'Arizona', stateRatePercent: 5.6, avgLocalRatePercent: 2.94, maxLocalRatePercent: 5.3, avgCombinedRatePercent: 8.54,
    hasLocalTaxes: true, sourcing: 'origin',
    marketplaceFacilitator: { effective: '2019-10-01', thresholdSalesUsd: 100_000, thresholdTransactions: null },
    shipping: 'exempt_if_separately_stated',
    exemptions: { groceries: { kind: 'exempt', note: 'State exempt; cities may tax.' }, motor_vehicle: DMV },
    notes: ['Transaction privilege tax (TPT) rather than a sales tax.', 'Period products and diapers taxed. Digital goods taxed.'],
  },
  {
    code: 'AR', name: 'Arkansas', stateRatePercent: 6.5, avgLocalRatePercent: 2.98, maxLocalRatePercent: 6.13, avgCombinedRatePercent: 9.48,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-07-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'taxable',
    exemptions: { groceries: { kind: 'exempt', note: 'State 0% from 2026-01-01 (was 0.125%).' }, motor_vehicle: DMV },
    notes: ['Period products and diapers taxed. Digital goods taxed.'],
    verify: ['Local sales tax on groceries after the 2026-01-01 state repeal.'],
  },
  {
    code: 'CA', name: 'California', stateRatePercent: 7.25, mandatoryLocalIncludedPercent: 1.25, avgLocalRatePercent: 1.78, maxLocalRatePercent: 5.25, avgCombinedRatePercent: 9.03,
    hasLocalTaxes: true, sourcing: 'hybrid',
    marketplaceFacilitator: { effective: '2019-10-01', thresholdSalesUsd: 500_000, thresholdTransactions: null },
    shipping: 'exempt_if_separately_stated', shippingNote: 'Exempt when separately stated, shipped by common carrier, and not above actual cost. Sellwild passes the label cost through unchanged, so exempt.',
    exemptions: { groceries: EXEMPT, digital_goods: { kind: 'exempt', note: 'Electronically delivered goods are not taxed.' }, motor_vehicle: DMV },
    notes: ['State, county and city portions source by origin; district taxes by destination.', 'Diapers and period products exempt.'],
  },
  {
    code: 'CO', name: 'Colorado', stateRatePercent: 2.9, avgLocalRatePercent: 4.99, maxLocalRatePercent: 9.1, avgCombinedRatePercent: 7.89,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-10-01', thresholdSalesUsd: 100_000, thresholdTransactions: null },
    shipping: 'exempt_if_separately_stated', shippingNote: 'The state exempts a separately stated delivery charge when the delivery is separable from the sale, which an itemised carrier label is. Exempt at the state level; the ~70 home-rule cities vary and are not modelled.',
    retailDeliveryFee: { feeCents: 31, minOrderCents: 0 },
    exemptions: { groceries: { kind: 'exempt', note: 'State exempt; locals may tax.' }, motor_vehicle: DMV },
    notes: ['Retail Delivery Fee $0.31 per order on taxable deliveries, 2026-07-01 to 2027-06-30.', 'Diapers and period products exempt since 2022-08-10. Digital goods taxed.'],
    verify: ['Home-rule cities\' own marketplace facilitator rules.'],
  },
  {
    code: 'CT', name: 'Connecticut', stateRatePercent: 6.35, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 6.35,
    hasLocalTaxes: false, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2018-12-01', thresholdSalesUsd: 250_000, thresholdTransactions: null, marketplaceThresholdNote: 'Marketplace facilitators: $250,000 facilitated sales in the prior 12 months (CGS 12-408e). Remote sellers: $100,000 AND 200 transactions.' },
    shipping: 'taxable',
    exemptions: {
      clothing: { kind: 'rate_above', thresholdCents: 100_000, ratePercent: 7.75, note: 'Luxury rate on clothing and footwear priced over $1,000.' },
      childrens_clothing: { kind: 'rate_above', thresholdCents: 100_000, ratePercent: 7.75 },
      groceries: EXEMPT, motor_vehicle: DMV,
    },
    notes: ['OTC drugs, diapers, period products and college textbooks exempt. Digital goods taxed at 6.35%.'],
  },
  {
    code: 'DE', name: 'Delaware', stateRatePercent: 0, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 0,
    hasLocalTaxes: false, sourcing: 'none', marketplaceFacilitator: null, shipping: 'not_applicable',
    notes: ['No sales tax (a gross receipts tax falls on sellers only).'],
  },
  {
    code: 'DC', name: 'District of Columbia', stateRatePercent: 6.0, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 6.0,
    hasLocalTaxes: false, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-04-01', thresholdSalesUsd: null, thresholdTransactions: null, marketplaceThresholdNote: 'No threshold for marketplace facilitators; every facilitator collects. Remote sellers: $100,000 or 200 transactions.' },
    shipping: 'exempt_if_separately_stated', shippingNote: 'Exempt when separately stated and transport occurs after the sale, which is every Sellwild shipment.',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['OTC drugs, diapers and period products exempt. Digital goods taxed.'],
    verify: ['Shipping conditions (one 2026 secondary source).'],
  },
  {
    code: 'FL', name: 'Florida', stateRatePercent: 6.0, avgLocalRatePercent: 0.98, maxLocalRatePercent: 2.0, avgCombinedRatePercent: 6.98,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2021-07-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: 'Taxable sales in the prior calendar year.' },
    shipping: 'taxable', shippingNote: 'Exempt only if the buyer could have avoided the charge by collecting in person. A shipped-only marketplace sale cannot, so taxable.',
    exemptions: { groceries: EXEMPT, digital_goods: EXEMPT, motor_vehicle: DMV },
    notes: [
      'Diapers and period products permanently exempt (2023).',
      'Baby and toddler clothing (sizes 5T and under, shoes 13T and under) is exempt, but the marketplace has no size data and Stripe\'s children\'s-clothing code carries no Florida exemption, so children\'s clothing is taxed here. That over-collects on true toddler sizes; it never under-collects.',
    ],
    verify: ['County surtax applies only to the first $5,000 of a single item.', 'OTC "common household remedies" exemption.'],
  },
  {
    code: 'GA', name: 'Georgia', stateRatePercent: 4.0, avgLocalRatePercent: 3.56, maxLocalRatePercent: 5.0, avgCombinedRatePercent: 7.56,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2020-04-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'taxable',
    exemptions: { groceries: { kind: 'exempt', note: 'State exempt; local rates apply.' }, motor_vehicle: DMV },
    notes: ['Period products and diapers taxed (2026 bill pending). Digital goods taxed since 2024-01-01.'],
  },
  {
    code: 'HI', name: 'Hawaii', stateRatePercent: 4.0, avgLocalRatePercent: 0.5, maxLocalRatePercent: 0.5, avgCombinedRatePercent: 4.5,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2020-01-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'taxable', shippingNote: 'General excise tax applies to all delivery charges.',
    exemptions: { motor_vehicle: DMV },
    notes: ['General Excise Tax (GET) with a broad base including services; groceries taxed at 4%.', 'Diapers and period products taxed.'],
  },
  {
    code: 'ID', name: 'Idaho', stateRatePercent: 6.0, avgLocalRatePercent: 0.03, maxLocalRatePercent: 3.0, avgCombinedRatePercent: 6.03,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-06-01', thresholdSalesUsd: 100_000, thresholdTransactions: null },
    shipping: 'exempt_if_separately_stated',
    exemptions: { motor_vehicle: DMV },
    notes: ['Groceries taxed at 6% (offset by an income-tax credit).', 'Diapers and period products taxed. Digital goods taxed.'],
  },
  {
    code: 'IL', name: 'Illinois', stateRatePercent: 6.25, avgLocalRatePercent: 2.73, maxLocalRatePercent: 4.75, avgCombinedRatePercent: 8.98,
    hasLocalTaxes: true, sourcing: 'origin',
    marketplaceFacilitator: { effective: '2020-01-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: '200-transaction test dropped 2026-01-01 for remote sellers and marketplaces alike.' },
    shipping: 'taxable', shippingNote: 'Exempt only if the buyer could have avoided the charge by collecting in person. Sellwild offers no pickup, so taxable.',
    exemptions: { groceries: { kind: 'exempt', note: 'State 1% grocery rate ended 2026-01-01; municipalities may impose their own 1%.' }, motor_vehicle: DMV },
    notes: ['Illinois-based sellers source by origin (ROT); remote sellers and marketplaces source by destination.', 'OTC drugs at a reduced 1% rate; period products exempt.'],
    verify: ['Reduced 1% rate on diapers.', 'Statewide digital-goods treatment versus the Chicago amusement tax.'],
  },
  {
    code: 'IN', name: 'Indiana', stateRatePercent: 7.0, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 7.0,
    hasLocalTaxes: false, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-07-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: '200-transaction test dropped 2024-01-01.' },
    shipping: 'taxable',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Digital goods taxed.'],
    verify: ['Period products and diapers taxability.'],
  },
  {
    code: 'IA', name: 'Iowa', stateRatePercent: 6.0, avgLocalRatePercent: 0.94, maxLocalRatePercent: 1.0, avgCombinedRatePercent: 6.94,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-01-01', thresholdSalesUsd: 100_000, thresholdTransactions: null },
    shipping: 'exempt_if_separately_stated',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Digital goods taxed.'],
    verify: ['Diapers and period products exemption effective date.'],
  },
  {
    code: 'KS', name: 'Kansas', stateRatePercent: 6.5, avgLocalRatePercent: 2.21, maxLocalRatePercent: 4.25, avgCombinedRatePercent: 8.71,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2021-07-01', thresholdSalesUsd: 100_000, thresholdTransactions: null },
    shipping: 'exempt_if_separately_stated', shippingNote: 'Since 2023-07-01.',
    exemptions: { groceries: { kind: 'exempt', note: 'State 0% since 2025-01-01; locals may apply.' }, motor_vehicle: DMV },
    notes: ['Period products and diapers taxed.'],
    verify: ['Digital downloads generally not taxed.'],
  },
  {
    code: 'KY', name: 'Kentucky', stateRatePercent: 6.0, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 6.0,
    hasLocalTaxes: false, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-07-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: '$100,000 or 200 transactions through 2026-07-31; $100,000 only from 2026-08-01 (HB 757).' },
    shipping: 'taxable',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Period products and diapers taxed (2026 lawsuit pending). Digital goods taxed.'],
  },
  {
    code: 'LA', name: 'Louisiana', stateRatePercent: 5.0, avgLocalRatePercent: 5.13, maxLocalRatePercent: 7.0, avgCombinedRatePercent: 10.13,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2020-07-01', thresholdSalesUsd: 100_000, thresholdTransactions: null },
    shipping: 'taxable', shippingNote: 'Taxable even when separately stated (Act 11, 2025-01-01) unless the buyer contracts directly with the carrier.',
    exemptions: { groceries: { kind: 'exempt', note: 'State exempt; parishes may tax.' }, motor_vehicle: DMV },
    notes: ['5% through 2029-12-31, then 4.75%.', 'Digital goods taxable from 2025-01-01.', 'Diapers and period products exempt at the state level.'],
    verify: ['Parish treatment of diapers and period products.'],
  },
  {
    code: 'ME', name: 'Maine', stateRatePercent: 5.5, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 5.5,
    hasLocalTaxes: false, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-10-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: 'Transaction test dropped 2022-01-01.' },
    shipping: 'exempt_if_separately_stated', shippingNote: 'Exempt when separately stated and shipped direct to the buyer by common carrier, contract carrier or USPS. A Shippo label meets this.',
    exemptions: { groceries: { kind: 'exempt', note: 'Grocery staples.' }, motor_vehicle: DMV },
    notes: ['Digital goods taxed.'],
    verify: ['Diapers and period products exemption.'],
  },
  {
    code: 'MD', name: 'Maryland', stateRatePercent: 6.0, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 6.0,
    hasLocalTaxes: false, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-10-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'exempt_if_separately_stated',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['OTC drugs, diapers (2022-07-01) and period products exempt. Digital goods taxed (2021).'],
  },
  {
    code: 'MA', name: 'Massachusetts', stateRatePercent: 6.25, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 6.25,
    hasLocalTaxes: false, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-10-01', thresholdSalesUsd: 100_000, thresholdTransactions: null },
    shipping: 'exempt_if_separately_stated', shippingNote: 'Exempt when separately stated and transport occurs after the sale, which is every Sellwild shipment.',
    exemptions: {
      clothing: { kind: 'exempt_below', thresholdCents: 17_500, excessOnly: true, note: 'Clothing under $175 per item exempt; only the excess over $175 is taxed. Athletic and protective apparel taxable.' },
      childrens_clothing: { kind: 'exempt_below', thresholdCents: 17_500, excessOnly: true },
      groceries: EXEMPT, motor_vehicle: DMV,
    },
    notes: ['Diapers, period products and textbooks exempt.'],
    verify: ['Digital goods: prewritten software taxed, ebooks and music generally not.'],
  },
  {
    code: 'MI', name: 'Michigan', stateRatePercent: 6.0, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 6.0,
    hasLocalTaxes: false, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2020-01-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'exempt_if_separately_stated', shippingNote: 'Since 2023-04-26 (PA 20/21 of 2023, RAB 2023-16); the seller must keep records showing the charge.',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Period products exempt (2022).'],
    verify: ['Diapers taxability.', 'Digital goods other than prewritten software.'],
  },
  {
    code: 'MN', name: 'Minnesota', stateRatePercent: 6.875, avgLocalRatePercent: 1.26, maxLocalRatePercent: 3.0, avgCombinedRatePercent: 8.14,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2018-10-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'taxable',
    retailDeliveryFee: { feeCents: 50, minOrderCents: 10_000 },
    exemptions: {
      clothing: { kind: 'exempt', note: 'General clothing exempt; fur, sports and protective gear taxable.' },
      childrens_clothing: EXEMPT, groceries: EXEMPT, motor_vehicle: DMV,
    },
    notes: ['Retail Delivery Fee $0.50 on orders of $100 or more (since 2024-07-01).', 'OTC drugs, diapers, period products and textbooks exempt. Digital goods taxed.', 'Marketplace providers never qualify for the isolated/occasional sale exemption (fs132).'],
  },
  {
    code: 'MS', name: 'Mississippi', stateRatePercent: 7.0, avgLocalRatePercent: 0.06, maxLocalRatePercent: 1.0, avgCombinedRatePercent: 7.06,
    hasLocalTaxes: true, sourcing: 'origin',
    marketplaceFacilitator: { effective: '2020-07-01', thresholdSalesUsd: 250_000, thresholdTransactions: null },
    shipping: 'taxable',
    exemptions: { groceries: { kind: 'reduced', ratePercent: 5.0, note: 'Reduced grocery rate since 2025-07-01.' }, motor_vehicle: DMV },
    notes: ['Diapers and period products taxed. Digital goods taxed.'],
  },
  {
    code: 'MO', name: 'Missouri', stateRatePercent: 4.225, avgLocalRatePercent: 4.22, maxLocalRatePercent: 6.25, avgCombinedRatePercent: 8.44,
    hasLocalTaxes: true, sourcing: 'origin',
    marketplaceFacilitator: { effective: '2023-01-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: 'Measured quarterly over the trailing 12 months.' },
    shipping: 'taxable', shippingNote: 'Exempt only when delivery is optional. Sellwild delivery is not optional, so taxable.',
    exemptions: { groceries: { kind: 'reduced', ratePercent: 1.225, note: 'State grocery rate 1.225%; locals apply.' }, digital_goods: EXEMPT, motor_vehicle: DMV },
    notes: ['Remote sales are subject to the vendor\'s use tax at the destination rate.', 'Diapers and period products exempt from 2025-08-28.'],
    verify: ['Shipping conditions (one 2026 secondary source).'],
  },
  {
    code: 'MT', name: 'Montana', stateRatePercent: 0, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 0,
    hasLocalTaxes: false, sourcing: 'none', marketplaceFacilitator: null, shipping: 'not_applicable',
    notes: ['No general sales tax (resort-area local taxes only).'],
  },
  {
    code: 'NE', name: 'Nebraska', stateRatePercent: 5.5, avgLocalRatePercent: 1.48, maxLocalRatePercent: 2.0, avgCombinedRatePercent: 6.98,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-04-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'taxable',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Digital goods taxed.'],
    verify: ['Period products and diapers taxability.'],
  },
  {
    code: 'NV', name: 'Nevada', stateRatePercent: 6.85, avgLocalRatePercent: 1.39, maxLocalRatePercent: 1.53, avgCombinedRatePercent: 8.24,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-10-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'exempt_if_separately_stated',
    exemptions: { groceries: EXEMPT, digital_goods: EXEMPT, motor_vehicle: DMV },
    notes: ['Diapers exempt 2025-01-01; period products exempt (2019 ballot).'],
  },
  {
    code: 'NH', name: 'New Hampshire', stateRatePercent: 0, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 0,
    hasLocalTaxes: false, sourcing: 'none', marketplaceFacilitator: null, shipping: 'not_applicable',
    notes: ['No sales tax.'],
  },
  {
    code: 'NJ', name: 'New Jersey', stateRatePercent: 6.625, avgLocalRatePercent: -0.02, maxLocalRatePercent: 0, avgCombinedRatePercent: 6.6,
    hasLocalTaxes: false, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2018-11-01', thresholdSalesUsd: null, thresholdTransactions: null, marketplaceThresholdNote: 'No threshold for marketplace facilitators; every facilitator collects. Remote sellers: $100,000 or 200 transactions.' },
    shipping: 'taxable',
    exemptions: {
      clothing: { kind: 'exempt', note: 'Accessories, fur, sports and protective gear taxable.' },
      childrens_clothing: EXEMPT, groceries: EXEMPT, motor_vehicle: DMV,
    },
    notes: ['Urban Enterprise Zone sellers collect at half rate (3.3125%), which is why the average local rate prints negative.', 'OTC drugs, diapers, period products and college textbooks exempt. Digital goods taxed.'],
    verify: ['Whether a marketplace facilitator has a threshold of its own here, or collects from the first dollar as the District of Columbia does. Secondary sources give $100,000 or 200 transactions for SELLERS and are silent on the facilitator itself. The conservative reading (no threshold) is kept until a primary source settles it, because it is the one that cannot under-collect.'],
  },
  {
    code: 'NM', name: 'New Mexico', stateRatePercent: 4.875, avgLocalRatePercent: 2.8, maxLocalRatePercent: 4.56, avgCombinedRatePercent: 7.68,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-07-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: 'Taxable gross receipts.' },
    shipping: 'taxable', shippingNote: 'Gross receipts tax.',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Gross Receipts Tax (GRT).', 'Diapers and period products exempt (2022). Digital goods taxed.'],
  },
  {
    code: 'NY', name: 'New York', stateRatePercent: 4.0, avgLocalRatePercent: 4.54, maxLocalRatePercent: 4.875, avgCombinedRatePercent: 8.54,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-06-01', thresholdSalesUsd: 500_000, thresholdTransactions: 100, thresholdJoin: 'and' },
    shipping: 'taxable', shippingNote: 'Taxable unless the charge is allocated between taxable and exempt items.',
    exemptions: {
      clothing: { kind: 'exempt_below', thresholdCents: 11_000, excessOnly: false, stateOnly: true, note: 'Items under $110 exempt from the 4% state tax; an item at $110 or more is fully taxable. NYC and some counties also exempt their local portion.' },
      childrens_clothing: { kind: 'exempt_below', thresholdCents: 11_000, excessOnly: false, stateOnly: true },
      groceries: EXEMPT, motor_vehicle: DMV,
    },
    notes: ['OTC drugs, diapers, period products and college textbooks exempt.'],
    verify: ['Downloaded ebooks/music/video are not taxed; prewritten software is.'],
  },
  {
    code: 'NC', name: 'North Carolina', stateRatePercent: 4.75, avgLocalRatePercent: 2.35, maxLocalRatePercent: 3.5, avgCombinedRatePercent: 7.1,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2020-02-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: 'Transaction test dropped 2024-07-01.' },
    shipping: 'taxable',
    exemptions: { groceries: { kind: 'reduced', ratePercent: 0, note: 'State exempt; the 2% local food rate applies.' }, motor_vehicle: DMV },
    notes: ['Period products and diapers taxed (2026 bill pending). Digital goods taxed.'],
  },
  {
    code: 'ND', name: 'North Dakota', stateRatePercent: 5.0, avgLocalRatePercent: 2.09, maxLocalRatePercent: 3.75, avgCombinedRatePercent: 7.09,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-10-01', thresholdSalesUsd: 100_000, thresholdTransactions: null },
    shipping: 'taxable',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Diapers exempt 2023-07-01; period products taxed.'],
    verify: ['Textbook exemption.', 'Digital goods generally not taxed.'],
  },
  {
    code: 'OH', name: 'Ohio', stateRatePercent: 5.75, avgLocalRatePercent: 1.54, maxLocalRatePercent: 2.25, avgCombinedRatePercent: 7.29,
    hasLocalTaxes: true, sourcing: 'origin',
    marketplaceFacilitator: { effective: '2019-09-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'taxable',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Diapers exempt 2023-10-01; period products exempt (2020). Digital goods taxed.'],
  },
  {
    code: 'OK', name: 'Oklahoma', stateRatePercent: 4.5, avgLocalRatePercent: 4.56, maxLocalRatePercent: 7.0, avgCombinedRatePercent: 9.06,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2018-07-01', thresholdSalesUsd: 10_000, thresholdTransactions: null, marketplaceThresholdNote: 'Marketplace facilitators: $10,000 in the prior 12 months triggers the election to collect or to report. Remote sellers: $100,000 taxable sales.' },
    shipping: 'exempt_if_separately_stated',
    exemptions: { groceries: { kind: 'exempt', note: 'State 0% since 2024-08-29; locals apply.' }, digital_goods: EXEMPT, motor_vehicle: DMV },
    notes: ['Period products and diapers taxed.', 'Marketplace-collected sales are excluded from a seller\'s own threshold.'],
  },
  {
    code: 'OR', name: 'Oregon', stateRatePercent: 0, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 0,
    hasLocalTaxes: false, sourcing: 'none', marketplaceFacilitator: null, shipping: 'not_applicable',
    notes: ['No sales tax.'],
  },
  {
    code: 'PA', name: 'Pennsylvania', stateRatePercent: 6.0, avgLocalRatePercent: 0.34, maxLocalRatePercent: 2.0, avgCombinedRatePercent: 6.34,
    hasLocalTaxes: true, sourcing: 'origin',
    marketplaceFacilitator: { effective: '2018-04-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: '$100,000 threshold from 2019-07-01.' },
    shipping: 'taxable',
    exemptions: {
      clothing: { kind: 'exempt', note: 'Most wearing apparel exempt; formal wear, fur and sporting apparel taxable.' },
      childrens_clothing: EXEMPT, groceries: EXEMPT, motor_vehicle: DMV,
    },
    notes: ['Local tax only in Philadelphia (2%) and Allegheny County (1%).', 'OTC drugs, diapers and period products exempt. Digital goods taxed (2016).'],
  },
  {
    code: 'RI', name: 'Rhode Island', stateRatePercent: 7.0, avgLocalRatePercent: 0, maxLocalRatePercent: 0, avgCombinedRatePercent: 7.0,
    hasLocalTaxes: false, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-07-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'taxable',
    exemptions: {
      clothing: { kind: 'exempt_below', thresholdCents: 25_000, excessOnly: true, note: 'Clothing up to $250 per item exempt; only the excess is taxed. Accessories taxable.' },
      childrens_clothing: { kind: 'exempt_below', thresholdCents: 25_000, excessOnly: true },
      groceries: EXEMPT, motor_vehicle: DMV,
    },
    notes: ['Diapers, period products and textbooks exempt. Digital goods taxed (2019).'],
  },
  {
    code: 'SC', name: 'South Carolina', stateRatePercent: 6.0, avgLocalRatePercent: 1.49, maxLocalRatePercent: 3.0, avgCombinedRatePercent: 7.49,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2018-11-01', thresholdSalesUsd: 100_000, thresholdTransactions: null },
    shipping: 'taxable',
    exemptions: { groceries: { kind: 'exempt', note: 'State exempt; locals may apply.' }, motor_vehicle: DMV },
    notes: ['Period products exempt 2024-05-13; diapers taxed; textbooks exempt.'],
    verify: ['Digital goods treatment.'],
  },
  {
    code: 'SD', name: 'South Dakota', stateRatePercent: 4.2, avgLocalRatePercent: 1.91, maxLocalRatePercent: 4.5, avgCombinedRatePercent: 6.11,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-03-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: 'Transaction test dropped 2023-07-01.' },
    shipping: 'taxable',
    exemptions: { motor_vehicle: DMV },
    notes: ['4.2% sunsets 2027-06-30 and reverts to 4.5%.', 'Broad base including services; groceries taxed at 4.2%.', 'Period products and diapers taxed. Digital goods taxed.'],
  },
  {
    code: 'TN', name: 'Tennessee', stateRatePercent: 7.0, avgLocalRatePercent: 2.61, maxLocalRatePercent: 2.75, avgCombinedRatePercent: 9.61,
    hasLocalTaxes: true, sourcing: 'origin',
    marketplaceFacilitator: { effective: '2020-10-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: 'Prior 12 months.' },
    shipping: 'taxable',
    exemptions: { groceries: { kind: 'reduced', ratePercent: 4.0, note: 'State grocery rate 4%; locals apply.' }, motor_vehicle: DMV },
    notes: ['Diapers taxed; textbooks exempt; digital goods taxed.'],
    verify: ['Period products taxability (sources conflict).', 'Local tax applies only to the first $1,600 of a single item.'],
  },
  {
    code: 'TX', name: 'Texas', stateRatePercent: 6.25, avgLocalRatePercent: 1.95, maxLocalRatePercent: 2.0, avgCombinedRatePercent: 8.2,
    hasLocalTaxes: true, sourcing: 'origin',
    marketplaceFacilitator: { effective: '2019-10-01', thresholdSalesUsd: 500_000, thresholdTransactions: null },
    shipping: 'taxable',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Remote sellers may elect the 1.75% single local use tax rate instead of the destination local rate.', 'OTC drugs with an FDA "Drug Facts" label, diapers and period products exempt (2023-09-01). Digital goods taxed.'],
  },
  {
    code: 'UT', name: 'Utah', stateRatePercent: 6.1, mandatoryLocalIncludedPercent: 1.25, avgLocalRatePercent: 1.32, maxLocalRatePercent: 4.7, avgCombinedRatePercent: 7.42,
    hasLocalTaxes: true, sourcing: 'origin',
    marketplaceFacilitator: { effective: '2019-10-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: 'Transaction test dropped 2025-07-01.' },
    shipping: 'exempt_if_separately_stated',
    exemptions: { groceries: { kind: 'reduced', ratePercent: 3.0, localsExempt: true, note: '1.75% state plus 1.25% mandatory local; the grocery repeal ballot failed.' }, motor_vehicle: DMV },
    notes: ['Period products and diapers taxed.', 'Digital goods taxed; SB 162 (2026-07-01) confirms streaming and subscription access are taxable.'],
  },
  {
    code: 'VT', name: 'Vermont', stateRatePercent: 6.0, avgLocalRatePercent: 0.43, maxLocalRatePercent: 1.0, avgCombinedRatePercent: 6.43,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-06-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'taxable',
    exemptions: {
      clothing: { kind: 'exempt', note: 'Accessories and equipment taxable.' },
      childrens_clothing: EXEMPT, groceries: EXEMPT, motor_vehicle: DMV,
    },
    notes: ['OTC drugs, diapers and period products exempt. Digital goods taxed.'],
  },
  {
    code: 'VA', name: 'Virginia', stateRatePercent: 5.3, mandatoryLocalIncludedPercent: 1.0, avgLocalRatePercent: 0.47, maxLocalRatePercent: 2.7, avgCombinedRatePercent: 5.77,
    hasLocalTaxes: true, sourcing: 'origin',
    marketplaceFacilitator: { effective: '2019-07-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'exempt_if_separately_stated', shippingNote: 'Exempt when separately stated, the transportation being after the sale.',
    exemptions: {
      groceries: { kind: 'reduced', ratePercent: 1.0, localsExempt: true, note: 'State portion 0 since 2023-01-01; the 1% local rate remains.' },
      digital_goods: EXEMPT, motor_vehicle: DMV,
    },
    notes: ['OTC drugs exempt; diapers and period products at the reduced 1% essential personal hygiene rate; textbooks exempt.'],
  },
  {
    code: 'WA', name: 'Washington', stateRatePercent: 6.5, avgLocalRatePercent: 3.07, maxLocalRatePercent: 4.2, avgCombinedRatePercent: 9.57,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2018-01-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: 'Cumulative gross income.' },
    shipping: 'taxable',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Diapers taxed (2026 exemption bill pending); period products exempt (2020). Digital goods taxed.'],
  },
  {
    code: 'WV', name: 'West Virginia', stateRatePercent: 6.0, avgLocalRatePercent: 0.6, maxLocalRatePercent: 1.4, avgCombinedRatePercent: 6.6,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-07-01', thresholdSalesUsd: 100_000, thresholdTransactions: 200 },
    shipping: 'taxable',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Period products and diapers taxed (2026 bill pending); textbooks exempt; digital goods taxed.'],
  },
  {
    code: 'WI', name: 'Wisconsin', stateRatePercent: 5.0, avgLocalRatePercent: 0.72, maxLocalRatePercent: 2.9, avgCombinedRatePercent: 5.72,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2020-01-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: 'Transaction test dropped 2021-02-20.' },
    shipping: 'taxable',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Period products and diapers taxed. Digital goods taxed.'],
  },
  {
    code: 'WY', name: 'Wyoming', stateRatePercent: 4.0, avgLocalRatePercent: 1.39, maxLocalRatePercent: 3.0, avgCombinedRatePercent: 5.39,
    hasLocalTaxes: true, sourcing: 'destination',
    marketplaceFacilitator: { effective: '2019-07-01', thresholdSalesUsd: 100_000, thresholdTransactions: null, marketplaceThresholdNote: 'Transaction test dropped 2024-07-01.' },
    shipping: 'exempt_if_separately_stated',
    exemptions: { groceries: EXEMPT, motor_vehicle: DMV },
    notes: ['Period products and diapers taxed. Digital goods taxed.'],
  },
];

/** Every rule, parsed once so a typo in the table fails at import, not at checkout. */
export const US_STATE_TAX_RULES: Readonly<Record<UsStateCode, UsStateTaxRule>> = Object.fromEntries(
  RAW_RULES.map((rule) => {
    const parsed = UsStateTaxRuleSchema.parse(rule);
    return [parsed.code, parsed];
  }),
) as Record<UsStateCode, UsStateTaxRule>;

export const US_TABLE_SOURCE = {
  rates: 'Tax Foundation, State and Local Sales Tax Rates, Midyear 2026 (rates as of 2026-07-01)',
  ratesUrl: 'https://taxfoundation.org/data/all/state/2026-sales-tax-rates-midyear/',
  compiledOn: '2026-09-11',
} as const;

export function getUsStateTaxRule(code: string): UsStateTaxRule | null {
  const parsed = UsStateCodeSchema.safeParse(code.trim().toUpperCase());
  return parsed.success ? US_STATE_TAX_RULES[parsed.data] : null;
}

/** States with no general sales tax at the state level. */
export const US_NO_SALES_TAX_STATES: readonly UsStateCode[] = ['AK', 'DE', 'MT', 'NH', 'OR'];
